import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, Sparkles, CheckCircle, Lock, Lightbulb } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useParticipantCount } from '../context/ParticipantContext';

const ExitIntentPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const { formatted: participantCount } = useParticipantCount();
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Don't show popup if:
    // 1. User is logged in
    // 2. Popup was already shown in this session
    // 3. User just came from assessment results page
    const popupShown = sessionStorage.getItem('exitPopupShown');
    const justCompletedAssessment = sessionStorage.getItem('assessmentJustCompleted');
    
    if (isAuthenticated || popupShown || justCompletedAssessment) {
      setHasShown(true);
      return;
    }

    // Don't show on certain pages
    const excludedPaths = ['/quickscan/start', '/quickscan/results', '/login', '/auth/callback'];
    if (excludedPaths.some(path => location.pathname.startsWith(path))) {
      return;
    }

    const handleMouseLeave = (e) => {
      // Only trigger when mouse leaves from the top of the page
      if (e.clientY <= 0 && !hasShown && !isAuthenticated) {
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exitPopupShown', 'true');
      }
    };

    // Also trigger after 30 seconds on mobile (no mouse leave detection)
    const timeoutId = setTimeout(() => {
      if (!hasShown && !isAuthenticated && window.innerWidth < 768) {
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exitPopupShown', 'true');
      }
    }, 30000);

    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      clearTimeout(timeoutId);
    };
  }, [hasShown, isAuthenticated, location.pathname]);

  const handleClose = () => {
    setIsVisible(false);
  };

  // Don't render if user is logged in or popup shouldn't show
  if (!isVisible || isAuthenticated) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Popup */}
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 animate-fade-in">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="h-6 w-6" />
        </button>

        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
            <Sparkles className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2">
          Wait! Before You Go...
        </h2>

        {/* Subtitle */}
        <p className="text-gray-600 text-center mb-6">
          Get your <span className="font-semibold text-gray-900">FREE AI Readiness Score</span> in just 5 minutes
        </p>

        {/* Benefits */}
        <div className="bg-blue-50/50 rounded-xl p-4 mb-6 space-y-3">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            <span className="text-gray-700">Compare your AI for HR with {participantCount} HR teams</span>
          </div>
          <div className="flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            <span className="text-gray-700">Get personalized AI recommendations</span>
          </div>
          <div className="flex items-center gap-3">
            <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
            <span className="text-gray-700">Discover concrete use cases for your situation</span>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="space-y-3">
          <Link to="/quickscan/start" onClick={handleClose}>
            <Button
              className="w-full py-6 text-lg font-semibold rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
            >
              <Lightbulb className="mr-2 h-5 w-5" />
              Start Quickscan
            </Button>
          </Link>
          
          <button
            onClick={handleClose}
            className="w-full text-gray-500 hover:text-gray-700 font-medium py-2 transition-colors"
          >
            Maybe Later
          </button>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 mt-4 text-sm text-gray-400">
          <Lock className="h-4 w-4" />
          <span>No credit card required • Takes 5 minutes</span>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
